import { Box, styled, Tooltip, useTheme } from '@mui/material';
import { SectionHeader } from '../AssetsPage/SectionHeader';
import { BodyText } from '../BodyText';
import { RefreshContents } from '../ContentsTable/RefreshContents';
import { AssetState } from '@/api/generatedApi';
import { useTenants } from '@/services/tenants/useTenants';
import { isValidAssetState } from '@/utils/assetStateDisplayText';

interface AssetStateCounts {
  indexing: number;
  finished: number;
  failed: number;
  unsupported: number;
  'no-3d-data': number;
  'missing-dependencies': number;
}

const SideBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0 18px',
});

const Legend = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const LegendItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  padding: '2px 4px',
  borderRadius: '4px',
  whiteSpace: 'nowrap',
});

const LegendColor = styled(Box)({
  width: '16px',
  height: '16px',
  borderRadius: '3px',
});

const stateColors: Partial<Record<AssetState, string>> = {
  finished: 'rgba(56, 119, 85, .95)',
  indexing: 'rgb(255, 152, 0)',
  failed: 'rgb(244, 67, 54)',
  unsupported: 'rgb(156, 39, 176)',
  'no-3d-data': 'rgb(96, 125, 139)',
  'missing-dependencies': 'rgb(255, 193, 7)',
};

const stateLabels: Partial<Record<AssetState, string>> = {
  finished: 'Finished',
  indexing: 'Indexing',
  failed: 'Failed',
  unsupported: 'Unsupported',
  'no-3d-data': 'No 3D Data',
  'missing-dependencies': 'Missing Dependencies',
};

export const AssetStateChart = ({
  onRefresh,
  selectedAssetState,
  data,
  onStateClick,
  assetsTotal,
}: {
  data: AssetStateCounts;
  assetsTotal: number;
  onStateClick?: (state: AssetState) => void;
  selectedAssetState?: AssetState;
  onRefresh?: () => void;
}) => {
  const theme = useTheme();
  const { tenant } = useTenants();
  const header = (
    <SectionHeader>
      <BodyText color='text.secondary'>Status</BodyText>
      <RefreshContents onRefresh={onRefresh} />
    </SectionHeader>
  );

  const assetsTotalText = (
    <Box display='flex' flexDirection='column' alignItems='center' marginTop={assetsTotal === 0 ? undefined : '-18px'}>
      <BodyText color='text.secondary' textAlign='center' marginTop='16px'>
        Assets total: {assetsTotal.toLocaleString()}
      </BodyText>
      {tenant?.assetLimit && (
        <BodyText color='text.secondary' textAlign='center' marginTop='4px'>
          Asset limit: {tenant.assetLimit.toLocaleString()}
        </BodyText>
      )}
    </Box>
  );

  if (assetsTotal === 0) {
    return (
      <SideBox>
        {header}
        {assetsTotalText}
      </SideBox>
    );
  }

  let currentAngle = 0;
  const segments = Object.entries(data)
    .filter(([key]) => isValidAssetState(key))
    .sort(([a], [b]) => {
      if (a === AssetState.Finished) return -1;
      if (b === AssetState.Finished) return 1;
      return 0;
    })
    .map(([state, count]) => {
      if (count === 0) return;

      const rawPercentage = (count / assetsTotal) * 100;
      const percentage = count === assetsTotal ? 100 : Math.max(0.01, Math.min(rawPercentage, 99.99));
      const angleSize = (count / assetsTotal) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + angleSize;

      const centerX = 100;
      const centerY = 100;
      const radius = 80;

      const startAngleRad = (startAngle - 90) * (Math.PI / 180);
      const endAngleRad = (endAngle - 90) * (Math.PI / 180);

      const startX = centerX + radius * Math.cos(startAngleRad);
      const startY = centerY + radius * Math.sin(startAngleRad);
      const endX = centerX + radius * Math.cos(endAngleRad);
      const endY = centerY + radius * Math.sin(endAngleRad);

      const largeArcFlag = angleSize > 180 ? 1 : 0;

      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${startX} ${startY}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        'Z',
      ].join(' ');

      currentAngle = endAngle;

      return {
        state: state as AssetState,
        count,
        percentage,
        pathData,
        color: stateColors[state as AssetState],
      };
    })
    .filter(Boolean);

  return (
    <SideBox>
      <Box>{header}</Box>
      <Box marginTop='6px' textAlign='center'>
        <Legend>
          {segments.map(segment => (
            <LegendItem
              key={segment!.state}
              onClick={() => onStateClick?.(segment!.state)}
              title={`View ${(stateLabels[segment!.state] ?? '').toLowerCase()} assets`}
              sx={{
                border:
                  selectedAssetState === segment!.state
                    ? `1px solid ${theme.palette.secondary.main}`
                    : '1px solid transparent',
              }}
            >
              <LegendColor sx={{ backgroundColor: segment!.color }} />
              <BodyText color='text.secondary' fontSize='13px'>
                {stateLabels[segment!.state]}:{' '}
                <span style={{ opacity: 0.7 }}>
                  {segment!.count.toLocaleString()} ({segment!.percentage.toFixed(2)}%)
                </span>
              </BodyText>
            </LegendItem>
          ))}
        </Legend>
        {segments.length === 1 ? (
          <svg width='200' height='200' viewBox='0 0 200 200' style={{ display: 'block', margin: '0 auto' }}>
            <Tooltip
              title={`${stateLabels[segments[0]!.state]}: ${segments[0]!.count.toLocaleString()} (${segments[0]!.percentage.toFixed(2)}%)`}
            >
              <circle
                cx='100'
                cy='100'
                r='80'
                fill={segments[0]!.color}
                stroke={theme.palette.background.paper}
                strokeWidth='0.5'
                style={{ cursor: 'pointer' }}
                onClick={() => onStateClick?.(segments[0]!.state)}
              />
            </Tooltip>
          </svg>
        ) : (
          <svg width='200' height='200' viewBox='0 0 200 200' style={{ display: 'block', margin: '0 auto' }}>
            {segments.map(segment => (
              <Tooltip
                key={segment!.state}
                title={`${stateLabels[segment!.state]}: ${segment!.count.toLocaleString()} (${segment!.percentage.toFixed(2)}%)`}
              >
                <g>
                  <path
                    d={segment!.pathData}
                    fill={segment!.color}
                    stroke={theme.palette.background.paper}
                    strokeWidth='0.5'
                    style={{ cursor: 'pointer' }}
                    onClick={() => onStateClick?.(segment!.state)}
                  />
                </g>
              </Tooltip>
            ))}
          </svg>
        )}

        {assetsTotalText}
      </Box>
    </SideBox>
  );
};
